import { Text } from 'react-native-paper';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';
import { Color } from '@/GlobalStyles';
import BaseContainer from '@/components/BaseContainer';
import Form from '@/components/Form/Form';
import {
  ActivitePreferentielleInsertInput,
  HabitudesInsertInput,
  useDeleteActivitePreferentielleByPatientMutation,
  useInsertActivitePreferentielleMutation,
  useInsertHabitudesMutation,
} from '@/data/data';
import FTextInput from '@/components/Form/TextInput';
import FormSectionFooter from '@/components/Form/FormSectionFooter';
import ScreenMargin from '@/components/ScreenMargin';
import { useUserContext } from '@/components/Auth/UserContext';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFormStyle from '@/components/Styles/useFormStyle';
import { FormSection } from '@/components/Form/FormSectionContext';
import { usePatientContext } from '@/components/Contexts/PatientContext';
import { useToast } from 'react-native-toast-notifications';
import TimePickerInput from '@/components/Form/TimePickerInput';
import DurationSliderInput from '@/components/Form/DurationSliderInput';
import DateUtils from '@/utils/DateUtils';
import FormSectionHeader from '@/components/Form/FormSectionHeader';
import SimpleLink from '@/components/SimpleLink';
import ObjectHelper from '@/utils/ObjectHelper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useTranslation } from 'react-i18next';
import HelpButton from '@/components/HelpButton';

type FormInputType = HabitudesInsertInput & {
  activitePreferentielles: ActivitePreferentielleInsertInput[];
};

const HabitudesPage = () => {
  const { detail } = useGlobalSearchParams();

  const { patient } = usePatientContext();
  const ref = useRef<typeof KeyboardAwareScrollView>();

  const { t } = useTranslation();

  const { user } = useUserContext();
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const [insertHabitudes] = useInsertHabitudesMutation();
  const [deleteActivitePreferentielleByPatient] =
    useDeleteActivitePreferentielleByPatientMutation();
  const [insertActivitePreferentielle] = useInsertActivitePreferentielleMutation();
  const form = useForm<FormInputType>();
  const [defaultValues, setDefaultValues] = useState({});

  useEffect(() => {
    const newDefaultValues = {
      ...ObjectHelper.createTimeValues({
        heureCoucher: '00:00',
        heureReveil: '00:00',
        tempsAutreVisionPres: '00:00',
        tempsLecture: '00:00',
        tempsSmartphone: '00:00',
        tempsTablette: '00:00',
        tempsTelevision: '00:00',
        tempsCuisine: '00:00',
        tempsJeuxSociete: '00:00',
        tempsConduite: '00:00',
        tempsOrdinateur: '00:00',
        tempsDiscussion: '00:00',
        tempsSieste: '00:00',
      }),
      ...ObjectHelper.createTimeValues(patient?.habitudes[0]),
      activitePreferentielles: patient?.activitePreferentielles || [],
    };
    setDefaultValues(newDefaultValues);
  }, [patient]);

  const styles = useFormStyle();

  const toast = useToast();

  const sommeTempsUtilise =
    DateUtils.getMinutes(form.watch('tempsAutreVisionPres')) +
    DateUtils.getMinutes(form.watch('tempsLecture')) +
    DateUtils.getMinutes(form.watch('tempsSmartphone')) +
    DateUtils.getMinutes(form.watch('tempsTablette')) +
    DateUtils.getMinutes(form.watch('tempsTelevision')) +
    DateUtils.getMinutes(form.watch('tempsCuisine')) +
    DateUtils.getMinutes(form.watch('tempsJeuxSociete')) +
    DateUtils.getMinutes(form.watch('tempsConduite')) +
    DateUtils.getMinutes(form.watch('tempsOrdinateur')) +
    DateUtils.getMinutes(form.watch('tempsDiscussion'));

  const minCoucher = DateUtils.getMinutes(form.watch('heureCoucher'));
  const minReveil = DateUtils.getMinutes(form.watch('heureReveil'));

  const sommeTempsDispo =
    minCoucher -
    minReveil -
    DateUtils.getMinutes(form.watch('tempsSieste')) +
    (minCoucher < minReveil ? 1440 : 0);

  const [nbActivitesPref, setNbActivitesPref] = useState();

  useEffect(() => {
    setNbActivitesPref(patient?.activitePreferentielles.length + 1);
  }, [patient]);

  const activitesPref = nbActivitesPref > 0 ? [...Array(nbActivitesPref).keys()] : [];

  return (
    <BaseContainer withScrollView={'keyboardAware'} ref={ref}>
      <ScreenMargin>
        <Form<FormInputType> {...form} defaultValues={defaultValues}>
          <FormSectionHeader displayForSection={[1, 2, 3, 4]}>
            <View style={style.barContainer}>
              {sommeTempsDispo > 0 ? (
                <>
                  <View
                    style={[
                      style.bar,
                      { width: `${(sommeTempsUtilise / sommeTempsDispo) * 100}%` },
                      sommeTempsUtilise / sommeTempsDispo > 1 ? { backgroundColor: 'red' } : null,
                    ]}
                  ></View>
                  <Text style={style.barText}>
                    {sommeTempsDispo > 0
                      ? `${DateUtils.displayHRTime(sommeTempsUtilise)} / ${DateUtils.displayHRTime(
                          sommeTempsDispo
                        )}`
                      : '00:00 / 00:00'}
                  </Text>
                </>
              ) : null}
            </View>
          </FormSectionHeader>
          <View style={styles?.bodyForm}>
            <FormSection sectionOrder={1}>
              <View style={styles?.groupForm}>
                <View style={styles?.titleGroupFormContainer}>
                  <Text style={styles?.titleGroupFormBase}>{t("Temps d'éveil")}</Text>
                  <HelpButton fieldName="tempsEveil" />
                </View>
                <TimePickerInput
                  name={'heureReveil'}
                  label={'Heure de réveil'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                />
                <TimePickerInput
                  name={'heureCoucher'}
                  label={'Heure de coucher'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                />
                <DurationSliderInput
                  name={'tempsSieste'}
                  label={'Temps de sieste'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={240}
                  stepMinutes={15}
                />
              </View>
            </FormSection>
            <FormSection sectionOrder={2}>
              <View style={styles?.groupForm}>
                <View style={styles?.titleGroupFormContainer}>
                  <Text style={styles?.titleGroupFormBase}>{t('Vision de près')}</Text>
                  <HelpButton fieldName="visionPres" />
                </View>
                <DurationSliderInput
                  name={'tempsLecture'}
                  label={'Temps de lecture par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
                <DurationSliderInput
                  name={'tempsTablette'}
                  label={'Temps de tablette par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
                <DurationSliderInput
                  name={'tempsSmartphone'}
                  label={'Temps de smartphone par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
                <DurationSliderInput
                  name={'tempsAutreVisionPres'}
                  label={"Temps d'autres activités de vision de près par jour"}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
              </View>
            </FormSection>
            <FormSection sectionOrder={3}>
              <View style={styles?.groupForm}>
                <View style={styles?.titleGroupFormContainer}>
                  <Text style={styles?.titleGroupFormBase}>{t('Vision intermédiaire')}</Text>
                  <HelpButton fieldName="visionIntermediaire" />
                </View>
                <DurationSliderInput
                  name={'tempsDiscussion'}
                  label={'Temps de discussion par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
                <DurationSliderInput
                  name={'tempsJeuxSociete'}
                  label={'Temps de jeux de société par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
                <DurationSliderInput
                  name={'tempsOrdinateur'}
                  label={"Temps d'utilisation de l'ordinateur par jour"}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
                <DurationSliderInput
                  name={'tempsCuisine'}
                  label={'Temps de cuisine par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
              </View>
            </FormSection>
            <FormSection sectionOrder={4}>
              <View style={styles?.groupForm}>
                <View style={styles?.titleGroupFormContainer}>
                  <Text style={styles?.titleGroupFormBase}>{t('Vision de loin')}</Text>
                  <HelpButton fieldName="visionLoin" />
                </View>
                <DurationSliderInput
                  name={'tempsConduite'}
                  label={'Temps de conduite par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
                <DurationSliderInput
                  name={'tempsTelevision'}
                  label={'Temps de télévision par jour'}
                  withHelpButton={true}
                  containerStyle={styles?.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
              </View>
            </FormSection>
            <FormSection sectionOrder={5}>
              <View style={styles?.groupForm}>
                <View style={styles?.titleGroupFormContainer}>
                  <Text style={styles?.titleGroupFormBase}>{t('Activités préférentielles')}</Text>
                  <HelpButton fieldName="activitesPreferentielles" />
                </View>
                {activitesPref.map((activite, index) => (
                  <View key={index} style={styles.rowForm}>
                    <Text style={styles?.titleSubGroupForm}>{`${t('Activité')} ${index + 1}`}</Text>
                    <FTextInput
                      name={`activitePreferentielles.${index}.nom`}
                      label={'Pourquoi le patient souhaite garder cette profondeur de champ ?'}
                      withHelpButton={true}
                      helpFieldName="activitePreferentielleName"
                      withMargin={false}
                      topLabel={true}
                      containerStyle={styles.input1Col}
                    />
                    <FTextInput
                      name={`activitePreferentielles.${index}.distanceMin`}
                      label={'Distance minimum (m)'}
                      withHelpButton={true}
                      helpFieldName="activitePreferentielleDistanceMin"
                      withMargin={false}
                      topLabel={true}
                      containerStyle={styles.input2Col}
                      keyboardType={'decimal-pad'}
                    />
                    <FTextInput
                      name={`activitePreferentielles.${index}.distanceMax`}
                      label={'Distance maximum (m)'}
                      withHelpButton={true}
                      helpFieldName="activitePreferentielleDistanceMax"
                      withMargin={false}
                      topLabel={true}
                      containerStyle={styles.input2Col}
                      keyboardType={'decimal-pad'}
                    />
                  </View>
                ))}
                <View style={styles.rowFormPadding}>
                  <SimpleLink onPress={() => setNbActivitesPref(nbActivitesPref + 1)}>
                    {'Ajouter une activité'}
                  </SimpleLink>
                  {nbActivitesPref > 1 ? (
                    <SimpleLink onPress={() => setNbActivitesPref(nbActivitesPref - 1)}>
                      {'Supprimer la dernière activité'}
                    </SimpleLink>
                  ) : null}
                </View>
              </View>
            </FormSection>
          </View>
          <View style={styles?.footerForm}>
            <FormSectionFooter
              saveLoading={loading}
              saveDisabled={loading}
              beforeChange={() => {
                if (ref.current) {
                  (ref.current as unknown as KeyboardAwareScrollView).scrollToPosition(0, 0);
                }
              }}
              onPress={form.handleSubmit(async (data) => {
                setLoading(true);
                if (user) {
                  await insertHabitudes({
                    fetchPolicy: 'no-cache',
                    variables: {
                      object: {
                        patientId: detail,
                        heureCoucher: DateUtils.getTimeSQLFormat(data?.heureCoucher),
                        heureReveil: DateUtils.getTimeSQLFormat(data?.heureReveil),
                        tempsAutreVisionPres: DateUtils.getTimeSQLFormat(
                          data?.tempsAutreVisionPres
                        ),
                        tempsConduite: DateUtils.getTimeSQLFormat(data?.tempsConduite),
                        tempsCuisine: DateUtils.getTimeSQLFormat(data?.tempsCuisine),
                        tempsDiscussion: DateUtils.getTimeSQLFormat(data?.tempsDiscussion),
                        tempsJeuxSociete: DateUtils.getTimeSQLFormat(data?.tempsJeuxSociete),
                        tempsLecture: DateUtils.getTimeSQLFormat(data?.tempsLecture),
                        tempsOrdinateur: DateUtils.getTimeSQLFormat(data?.tempsOrdinateur),
                        tempsSieste: DateUtils.getTimeSQLFormat(data?.tempsSieste),
                        tempsSmartphone: DateUtils.getTimeSQLFormat(data?.tempsSmartphone),
                        tempsTablette: DateUtils.getTimeSQLFormat(data?.tempsTablette),
                        tempsTelevision: DateUtils.getTimeSQLFormat(data?.tempsTelevision),
                      },
                    },
                  });
                  await deleteActivitePreferentielleByPatient({
                    fetchPolicy: 'no-cache',
                    variables: {
                      patientId: detail,
                    },
                  });
                  await Promise.all(
                    data.activitePreferentielles.map(async (activite) => {
                      if (
                        activite.nom !== '' &&
                        activite.nom !== undefined &&
                        activite.nom !== null
                      ) {
                        await insertActivitePreferentielle({
                          fetchPolicy: 'no-cache',
                          variables: {
                            object: {
                              patientId: detail,
                              nom: activite?.nom,
                              distanceMin: activite?.distanceMin,
                              distanceMax: activite?.distanceMax,
                            },
                          },
                        });
                      }
                    })
                  );
                  toast.show(t('Habitudes enregistrées'), {
                    type: 'success',
                    placement: 'center',
                    duration: 1000,
                    animationType: 'slide-in',
                    onPress: (id) => {
                      toast.hide(id);
                    },
                  });
                  router.push(`/patients/${detail}/implants`);
                }
                setLoading(false);
              })}
            />
          </View>
        </Form>
      </ScreenMargin>
    </BaseContainer>
  );
};

export default HabitudesPage;

const style = StyleSheet.create({
  barContainer: {
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderStyle: 'solid',
    borderRadius: 20,
    height: 40,
    width: '90%',
    padding: 2,
    position: 'relative',
  },
  bar: {
    backgroundColor: Color.MainBleu,
    borderRadius: 17,
    height: 34,
  },
  barText: {
    position: 'absolute',
    color: Color.VertElectrique,
    fontWeight: 'bold',
    fontSize: 20,
    top: 7,
    left: 10,
  },
});
